export const SUBMISSION_SEARCH_STATUS = {
  onTime: 'onTime',
  late: 'late',
  noSubmission: 'noSubmission',
} as const;

export type SubmissionSearchStatus =
  typeof SUBMISSION_SEARCH_STATUS[keyof typeof SUBMISSION_SEARCH_STATUS];

export type SubmissionSearchItem = {
  id?: string;
  user?: {
    id?: string | number;
    firstName?: string;
    lastName?: string;
  };
};

export type SubmissionSearchGroups<T extends SubmissionSearchItem> = {
  earlySubmissions: T[];
  lateSubmissions: T[];
  noSubmissions: T[];
};

export type SubmissionSearchResult<T extends SubmissionSearchItem> = {
  submission: T;
  status: SubmissionSearchStatus;
  statusLabel: 'On Time' | 'Late' | 'No Submission';
};

export const getSubmissionStudentName = (
  submission: SubmissionSearchItem
): string =>
  [submission.user?.firstName, submission.user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

export const createSubmissionSearchResults = <T extends SubmissionSearchItem>(
  groups: SubmissionSearchGroups<T>
): SubmissionSearchResult<T>[] => [
  ...groups.earlySubmissions.map((submission) => ({
    submission,
    status: SUBMISSION_SEARCH_STATUS.onTime,
    statusLabel: 'On Time' as const,
  })),
  ...groups.lateSubmissions.map((submission) => ({
    submission,
    status: SUBMISSION_SEARCH_STATUS.late,
    statusLabel: 'Late' as const,
  })),
  ...groups.noSubmissions.map((submission) => ({
    submission,
    status: SUBMISSION_SEARCH_STATUS.noSubmission,
    statusLabel: 'No Submission' as const,
  })),
];

export const filterSubmissionSearchResults = <T extends SubmissionSearchItem>(
  results: SubmissionSearchResult<T>[],
  query: string,
  getStudentName: (submission: T) => string = getSubmissionStudentName
): SubmissionSearchResult<T>[] => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) return results;
  return results.filter(({ submission }) =>
    getStudentName(submission).toLowerCase().includes(normalizedQuery)
  );
};
